<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn icon class="mr-2" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-avatar size="40" color="primary" class="mr-3" rounded>
        <v-icon color="white">mdi-bug-outline</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <div class="text-h6 font-weight-bold">All Bug Tickets</div>
        <div class="caption grey--text">
          {{ tickets.length === 0 ? 'No tickets yet' : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` }}
        </div>
      </div>
      <v-btn small outlined color="primary" class="text-none rounded-lg mr-2" :loading="loading" @click="load">
        <v-icon small left>mdi-refresh</v-icon>Refresh
      </v-btn>
      <v-btn small color="primary" depressed class="text-none rounded-lg" @click="openDialog">
        <v-icon small left>mdi-plus</v-icon>Report a bug
      </v-btn>
    </div>

    <!-- Loading -->
    <div v-if="loading && tickets.length === 0" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Empty state -->
    <v-card v-else-if="tickets.length === 0" outlined class="rounded-lg">
      <v-card-text class="text-center py-12">
        <v-icon size="48" color="grey lighten-1">mdi-bug-outline</v-icon>
        <div class="body-1 grey--text mt-3">No bug tickets yet.</div>
        <v-btn color="primary" depressed class="text-none rounded-lg mt-4" @click="openDialog">
          <v-icon small left>mdi-plus</v-icon>Report your first bug
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Ticket list (full detail) -->
    <v-card
      v-for="t in tickets"
      :key="t.id"
      outlined
      class="rounded-lg mb-4"
    >
      <v-card-text>
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center" style="gap:10px;">
            <span class="caption font-weight-bold grey--text" style="font-family:monospace;">{{ t.number }}</span>
            <v-chip x-small :color="severityColor(t.severity)" class="font-weight-medium">{{ t.severity }}</v-chip>
            <v-chip
              x-small
              :color="statusColor(t.status)"
              class="font-weight-medium"
              style="cursor:pointer;"
              @click="cycleStatus(t)"
            >
              {{ statusLabel(t.status) }}
            </v-chip>
          </div>
          <v-btn
            x-small
            depressed
            color="red lighten-4"
            class="red--text text--darken-2 rounded-lg text-none px-2"
            @click="remove(t.id)"
          >
            <v-icon x-small left>mdi-delete-outline</v-icon>Delete
          </v-btn>
        </div>

        <div class="subtitle-1 font-weight-bold mb-2">{{ t.title }}</div>
        <div
          v-if="t.description"
          class="body-2 grey--text text--darken-2 mb-3"
          style="line-height:1.6;white-space:pre-wrap;"
        >{{ t.description }}</div>

        <!-- Screenshots -->
        <div v-if="t.screenshots && t.screenshots.length" class="mt-3">
          <div class="caption grey--text text-uppercase font-weight-medium mb-2">
            Screenshots ({{ t.screenshots.length }})
          </div>
          <div class="d-flex flex-wrap" style="gap:10px;">
            <a
              v-for="(s, i) in t.screenshots"
              :key="i"
              :href="screenshotUrl(s)"
              target="_blank"
              rel="noopener"
              class="d-block rounded-lg"
              style="width:140px;height:140px;overflow:hidden;border:1px solid #e0e0e0;background:#fff;"
            >
              <img
                :src="screenshotUrl(s)"
                :alt="s.name || 'screenshot'"
                style="width:100%;height:100%;object-fit:cover;"
              />
            </a>
          </div>
        </div>

        <div class="caption grey--text mt-3" style="font-size:11px;">
          Reported {{ formatDate(t.date) }}
        </div>
      </v-card-text>
    </v-card>

    <!-- Report Bug Dialog -->
    <v-dialog v-model="dialog" max-width="480">
      <v-card class="rounded-lg">
        <v-card-title class="subtitle-1 font-weight-bold">Report a Bug</v-card-title>
        <v-card-text>
          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Title</div>
          <v-text-field v-model="draft.title" dense outlined hide-details class="mb-3 rounded-lg" />
          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Description</div>
          <v-textarea v-model="draft.description" dense outlined hide-details rows="3" class="mb-3 rounded-lg" />
          <div class="caption grey--text text-uppercase font-weight-medium mb-1">Severity</div>
          <v-select v-model="draft.severity" :items="['low','medium','high']" dense outlined hide-details class="rounded-lg mb-3" />

          <div class="caption grey--text text-uppercase font-weight-medium mb-1">
            Screenshots <span class="text-lowercase grey--text">(optional)</span>
          </div>
          <v-file-input
            v-model="draft.screenshots"
            multiple
            dense
            outlined
            hide-details
            show-size
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            prepend-icon=""
            prepend-inner-icon="mdi-image-multiple-outline"
            placeholder="Attach one or more images"
            class="rounded-lg"
            :disabled="submitting"
          >
            <template v-slot:selection="{ text, index }">
              <v-chip small color="primary" outlined close @click:close="removeScreenshot(index)">
                <v-icon x-small left>mdi-image-outline</v-icon>
                {{ text }}
              </v-chip>
            </template>
          </v-file-input>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text class="text-none grey--text" :disabled="submitting" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed class="text-none rounded-lg" :loading="submitting" @click="submit">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'BugTicketsView',
  data() {
    return {
      loading: false,
      submitting: false,
      dialog: false,
      draft: { title: '', description: '', severity: 'medium', screenshots: [] },
      tickets: [],
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push('/employee-growth');
    },
    openDialog() {
      this.draft = { title: '', description: '', severity: 'medium', screenshots: [] };
      this.dialog = true;
    },
    removeScreenshot(index) {
      const next = [...(this.draft.screenshots || [])];
      next.splice(index, 1);
      this.draft.screenshots = next;
    },
    mapTicket(t) {
      return {
        id: t.id,
        number: t.ticket_number,
        title: t.title,
        description: t.description,
        severity: t.severity,
        status: t.status,
        screenshots: Array.isArray(t.screenshots) ? t.screenshots : [],
        date: t.created_at,
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/bugs');
        if (res.data.response) {
          this.tickets = res.data.data.map(this.mapTicket);
        }
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.draft.title.trim()) { this.$toast && this.$toast.error('Add a title'); return; }
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append('title', this.draft.title);
        fd.append('description', this.draft.description || '');
        fd.append('severity', this.draft.severity || 'medium');
        (this.draft.screenshots || []).forEach((f) => {
          if (f instanceof File) fd.append('screenshots[]', f);
        });

        const res = await this.$axios.post('api/employee-growth/bugs', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.data.response) {
          this.tickets.unshift(this.mapTicket(res.data.data));
          this.draft = { title: '', description: '', severity: 'medium', screenshots: [] };
          this.dialog = false;
          this.$toast && this.$toast.success('Bug ticket submitted!');
        }
      } catch (e) {
        this.$toast && this.$toast.error('Failed to create ticket');
      } finally {
        this.submitting = false;
      }
    },
    screenshotUrl(s) {
      if (!s) return '';
      if (typeof s === 'string') return s;
      if (s.url) {
        if (s.url.startsWith('http')) return s.url;
        const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
        return base + s.url;
      }
      return '';
    },
    async cycleStatus(t) {
      const order = ['open', 'in_progress', 'resolved'];
      const nextStatus = order[(order.indexOf(t.status) + 1) % order.length];
      try {
        const res = await this.$axios.post(`api/employee-growth/bugs/${t.id}`, { status: nextStatus });
        if (res.data.response) t.status = nextStatus;
      } catch (e) { /* silently fail */ }
    },
    async remove(id) {
      try {
        await this.$axios.delete(`api/employee-growth/bugs/${id}`);
        this.tickets = this.tickets.filter(t => t.id !== id);
      } catch (e) { this.$toast && this.$toast.error('Failed to delete ticket'); }
    },
    statusLabel(s) { return { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved' }[s] || s; },
    statusColor(s) { return { open: 'amber lighten-3', in_progress: 'blue lighten-4', resolved: 'green lighten-4' }[s] || 'grey lighten-3'; },
    severityColor(s) { return { low: 'grey lighten-3', medium: 'blue lighten-4', high: 'red lighten-4' }[s] || 'grey lighten-3'; },
    formatDate(iso) {
      // $date/$tz so this follows the display timezone and clock settings.
      const d = this.$date(iso);
      if (!d.isValid()) return iso;
      return d.format(`MMM D, YYYY ${this.$tz.time}`);
    },
  },
};
</script>
